import React, { useEffect, useState, useMemo } from 'react';
import Chart from 'react-apexcharts';
import styles from './Graphs.module.css';
import { useAnalysis } from '../../contexts/AnalysisContext';
import { useTranslation } from "react-i18next";

const indexTextMap = {
  NDVI: {
    label: 'Crop Health',
    yTitle: 'Crop Health Values',
    chartTitle: 'Crop Health Over Time',
  },
  NDRE: {
    label: 'Fertilizer Content',
    yTitle: 'Fertilizer Content Values',
    chartTitle: 'Fertilizer Content Over Time',
  },
  NDMI: {
    label: 'Moisture Content',
    yTitle: 'Moisture Content Values',
    chartTitle: 'Moisture Content Over Time',
  },
  MSAVI: {
    label: 'Germination & Initial Growth',
    yTitle: 'Germination & Initial Growth Values',
    chartTitle: 'Germination & Initial Growth Over Time',
  },
  NCC: {
    label: 'Natural Image',
    yTitle: 'Natural Image Values',
    chartTitle: 'Natural Image Over Time',
  },
};

const Graphs = ({ selectedIndex, isLoading }) => {
  const [chartData, setChartData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => JSON.parse(localStorage.getItem('SelectedDate')) || '');
  const [showAlert, setShowAlert] = useState(false);
  const [alertData, setAlertData] = useState(null);
  const { data } = useAnalysis();
  const { t } = useTranslation();

  useEffect(() => {
    if (!isLoading && data) {
      data
        .then((result) => {
          processChartData(result.results, selectedIndex);
        })
        .catch((error) => {
          console.error('Error processing data:', error);
        });
    }
  }, [selectedIndex, isLoading, data]);

  useEffect(() => {
    if (!selectedDate || !data || isLoading || selectedIndex !== 'NDVI') return;

    data
      .then((result) => {
        processChartData(result.results, selectedIndex);
      })
      .catch((error) => {
        console.error('Error processing data:', error);
      });
  }, [selectedDate, selectedIndex, isLoading, data]);


  const checkForAlert = (sortedData, currentDate) => {
    if (selectedIndex !== 'NDVI' || !currentDate) return null;

    // Find the exact data point for the selected date
    const currentDataPoint = sortedData.find(item => {
      const itemDate = new Date(item.Date).toISOString().split('T')[0];
      return itemDate === currentDate;
    });

    if (!currentDataPoint) return null;

    const currentIndex = sortedData.indexOf(currentDataPoint);

    // Check if we have previous and next data points
    if (currentIndex === -1 || currentIndex === 0 || currentIndex === sortedData.length - 1) {
      return null;
    }

    const current = currentDataPoint;
    const previous = sortedData[currentIndex - 1];
    const next = sortedData[currentIndex + 1];

    const currentAvg = current.Average;
    const prevAvg = previous.Average;
    const nextAvg = next.Average;

    console.log('Alert Check:', {
      currentDate: currentDate,
      currentAvg,
      prevAvg,
      nextAvg,
      condition: currentAvg < 0.15 && prevAvg > 0.35 && nextAvg > 0.35
    });

    // Check if current average is below 0.15 and both previous/next are above 0.35
    if (currentAvg < 0.15 && prevAvg > 0.35 && nextAvg > 0.35) {
      return {
        currentDate: new Date(current.Date).toLocaleDateString('en-GB', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        }),
        currentValue: currentAvg,
        previousValue: prevAvg,
        nextValue: nextAvg
      };
    }

    return null;
  };

  const processChartData = (data, indexType) => {
    const filteredData = data?.filter((item) => item.IndexType === indexType);
    const sortedData = filteredData.sort(
      (a, b) => new Date(a.Date) - new Date(b.Date)
    );

    if (selectedDate && indexType === 'NDVI' && sortedData.length > 0) {
      const [day, month, year] = selectedDate.split('-');
      const selectedDateFormatted = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;

      const alertInfo = checkForAlert(sortedData, selectedDateFormatted);

      // ✅ Only show the alert if the selected date still matches
      if (alertInfo && selectedDateFormatted === selectedDateFormatted) {
        console.log('Alert triggered:', alertInfo);
        setAlertData(alertInfo);
        setShowAlert(true);
      } else {
        setShowAlert(false);
      }
    } else {
      setShowAlert(false);
    }


    const series = [
      {
        name: 'Average',
        data: sortedData.map((item) => ({
          x: new Date(item.Date),
          y: item.Average,
        })),
      },
    ];

    setChartData({ series });
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const stored = JSON.parse(localStorage.getItem('SelectedDate'));
      // Only update if it’s different and not empty
      if (stored && stored !== selectedDate) {
        setSelectedDate(stored);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [selectedDate]);


  const handleCloseAlert = () => {
    setShowAlert(false);
  };

  const options = useMemo(() => {
    let reversedDate = '';

    if (selectedDate) {
      const [day, month, year] = selectedDate.split('-');
      reversedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }

    return {
      chart: {
        type: 'line',
        toolbar: {
          show: true,
          tools: {
            download: true,
          },
          export: {
            csv: {
              filename: indexTextMap[selectedIndex]?.chartTitle || `${selectedIndex} Over Time`,
              headerCategory: 'Date',
              headerValue: indexTextMap[selectedIndex]?.yTitle || `${selectedIndex} Values`,
              dateFormatter(timestamp) {
                const date = new Date(timestamp);
                return date.toLocaleDateString('en-GB', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                });
              },
              valueFormatter(value) {
                return value.toFixed(3);
              },
            },
          },
        },
        background: '#ffffff',
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
        },
      },
      annotations: {
        xaxis: [
          {
            x: new Date(reversedDate).getTime(),
            borderColor: '#008FFB',
            strokeDashArray: 4,
            label: {
              borderColor: '#008FFB',
              style: {
                color: '#fff',
                background: '#008FFB',
              },
              text: `Marker: ${new Date(reversedDate).toLocaleString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })}`,
            },
          },
        ],
      },
      xaxis: {
        type: 'datetime',
        labels: {
          format: 'MMM yyyy',
          style: {
            fontSize: '12px',
            colors: '#666',
          },
        },
        title: {
          text: 'Date',
          style: {
            fontSize: '14px',
            fontWeight: 'bold',
            color: '#333',
          },
        },
        axisBorder: {
          show: true,
          color: '#cccccc',
        },
        axisTicks: {
          show: true,
          color: '#cccccc',
        },
      },
      yaxis: {
        min: -1,
        max: 1,
        labels: {
          formatter: (value) => value.toFixed(2),
          style: {
            fontSize: '10px',
            colors: '#666',
          },
        },
        title: {
          text: indexTextMap[selectedIndex]?.yTitle || `${selectedIndex} Values`,
          style: {
            fontSize: '14px',
            fontWeight: 'bold',
            color: '#333',
          },
        },
        axisBorder: {
          show: true,
          color: '#cccccc',
        },
        axisTicks: {
          show: true,
          color: '#cccccc',
        },
      },
      tooltip: {
        theme: 'light',
        x: {
          format: 'dd MMM yyyy', // 👈 this shows full date like "05 Aug 2025"
        },
        y: {
          formatter: (value) => value.toFixed(2),
        },
        marker: {
          show: true,
        },
      },
      stroke: {
        curve: 'smooth',
        width: 2,
        colors: ['#1841d9'],
      },
      markers: {
        size: 5,
        colors: ['#1841d9'],
        strokeColors: '#ffffff',
        strokeWidth: 2,
        hover: {
          size: 7,
        },
      },
      title: {
        text: indexTextMap[selectedIndex]?.chartTitle || `${selectedIndex} Values Over Time`,
        align: 'center',
        margin: 10,
        style: {
          fontSize: '18px',
          fontWeight: 'bold',
          color: 'black',
        },
      },
      grid: {
        show: true,
        borderColor: '#f1f1f1',
        strokeDashArray: 4,
      },
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'center',
        labels: {
          colors: '#666',
          useSeriesColors: false,
        },
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            chart: {
              height: 200,
            },
          },
        },
        {
          breakpoint: 480,
          options: {
            chart: {
              height: 150,
            },
          },
        },
      ],
    };
  }, [selectedIndex, selectedDate]);

  return (
    <>
      {/* Centered Crop Health Alert Modal */}
      {showAlert && alertData && (
        <div
          onClick={handleCloseAlert} // clicking outside closes the alert
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
        >
          <div
            className="bg-yellow-50 border-l-8 border-yellow-500 p-6 rounded-xl shadow-2xl max-w-md w-full mx-4 relative animate-fade-in"
            onClick={(e) => e.stopPropagation()} // prevent closing when clicking inside
          >
            <button
              onClick={handleCloseAlert}
              className="absolute top-3 right-3 text-yellow-700 hover:text-yellow-900 transition-colors"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <div className="flex items-start space-x-3">
              <div className="text-3xl">🌾</div>
              <div>
                <h3 className="text-lg font-semibold text-yellow-800">
                  Crop Health Alert!
                </h3>
                <p className="mt-2 text-sm text-yellow-700 whitespace-pre-line leading-relaxed">
                  {`On ${alertData.currentDate}, Crop Health value is quite low (${alertData.currentValue.toFixed(2)}). This sudden drop may be caused by cloud interference or shadowing in the area.\n\nOur AI model compared nearby images and found higher Crop Health values before and after this date, indicating healthy crop conditions.`}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Main Graph Component */}
      <div
        className={`bg-[#dbeafe] p-2 rounded-md flex flex-col gap-4 items-center justify-center 
    ${!chartData
            ? "sm:p-2 sm:w-full sm:h-[calc(32vh)] md:p-2 md:w-[100%] md:h-[calc(40vh)] lg:p-2 lg:w-[100%] lg:h-[calc(45vh)] xl:p-2 xl:w-[100%] xl:h-[calc(44.5vh)] 2xl:p-2 2xl:w-[100%] 2xl:h-[calc(41.0vh)] 2.5xl:p-2 2.5xl:w-[100%] 2.5xl:h-[calc(35.5vh)] 3xl:p-2 3xl:w-[100%] 3xl:h-[calc(35.5vh)]"
            : "sm:p-2 sm:w-full sm:h-[calc(50vh)] md:p-2 md:w-[100%] md:h-[calc(45vh)] lg:p-2 lg:w-[100%] lg:h-[calc(50vh)] xl:p-2 xl:w-[100%] xl:h-[calc(44.4vh)] 2xl:p-2 2xl:w-[100%] 2xl:h-[calc(44vh)] 2.5xl:p-2 2.5xl:w-[100%] 2.5xl:h-[calc(35.3vh)]"
          }`}
      >

        {isLoading || !chartData ? (
          <div className="w-full flex items-center justify-center h-[37vh]">
            <div className="flex flex-col items-center justify-center">
              <div className="tooltip" data-tip="Select field from saved field button">
                <div className={`${styles.loader}`} />
              </div>
              <div className="mt-2 text-center text-sm">
                {t("Select your field from the saved field button")}
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full flex items-center justify-center">
            <div className="w-full max-w-[700px] max-h-[282px]">
              <Chart
                type="line"
                options={options}
                series={chartData.series}
                height="283"
                width="100%"
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Graphs;